/**
 * Geometry Exporter
 * 
 * Exports BIM geometry to various formats including BREP and tessellation.
 */

import * as THREE from 'three';

// ==================== Types ====================

export type ExportFormat = 'OBJ' | 'STL' | 'GLTF' | 'IFC' | 'SAT';

export interface ExportOptions {
    format: ExportFormat;
    includeCategories?: string[];
    excludeCategories?: string[];
    tessellationQuality: 'Low' | 'Medium' | 'High';
    exportMaterials: boolean;
    mergeByCategory: boolean;
    coordinateSystem: 'Local' | 'Project' | 'Survey';
}

export interface ExportResult {
    format: ExportFormat;
    data: string | ArrayBuffer;
    elementCount: number;
    triangleCount: number;
    fileSize: number;
}

// ==================== Tessellation Quality ====================

const TESSELLATION_SETTINGS = {
    Low: { segments: 8, curveSegments: 6 },
    Medium: { segments: 16, curveSegments: 12 },
    High: { segments: 32, curveSegments: 24 }
};

// ==================== Geometry Exporter ====================

export class GeometryExporter {
    private options: ExportOptions = {
        format: 'OBJ',
        tessellationQuality: 'Medium',
        exportMaterials: true,
        mergeByCategory: false,
        coordinateSystem: 'Project'
    };

    public setOptions(options: Partial<ExportOptions>): void {
        Object.assign(this.options, options);
    }

    // ==================== Export Methods ====================

    public exportElements(elements: any[]): ExportResult {
        // Filter elements
        let filtered = elements;

        if (this.options.includeCategories?.length) {
            filtered = filtered.filter(e => this.options.includeCategories!.includes(e.category));
        }

        if (this.options.excludeCategories?.length) {
            filtered = filtered.filter(e => !this.options.excludeCategories!.includes(e.category));
        }

        // Generate meshes
        const meshes: THREE.Mesh[] = [];
        for (const element of filtered) {
            const mesh = this.elementToMesh(element);
            if (mesh) meshes.push(mesh);
        }

        // Merge if needed
        if (this.options.mergeByCategory) {
            // Group by category and merge
        }

        // Export based on format
        let data: string | ArrayBuffer;
        let triangleCount = 0;

        switch (this.options.format) {
            case 'OBJ':
                data = this.exportToOBJ(meshes);
                break;
            case 'STL':
                data = this.exportToSTL(meshes);
                break;
            default:
                data = this.exportToOBJ(meshes);
        }

        // Count triangles
        for (const mesh of meshes) {
            const geometry = mesh.geometry as THREE.BufferGeometry;
            if (geometry.index) {
                triangleCount += geometry.index.count / 3;
            } else {
                const position = geometry.getAttribute('position');
                if (position) {
                    triangleCount += position.count / 3;
                }
            }
        }

        return {
            format: this.options.format,
            data,
            elementCount: filtered.length,
            triangleCount,
            fileSize: typeof data === 'string' ? data.length : (data as ArrayBuffer).byteLength
        };
    }

    // ==================== Element to Mesh ====================

    private elementToMesh(element: any): THREE.Mesh | null {
        const gd = element.geometryData;
        if (!gd) return null;

        const settings = TESSELLATION_SETTINGS[this.options.tessellationQuality];
        let geometry: THREE.BufferGeometry | null = null;

        if (gd.start && gd.end) {
            // Linear element (beam, duct, pipe)
            geometry = this.createExtrudedGeometry(gd, settings);
        } else if (gd.boundary) {
            // Surface element (floor, wall)
            geometry = this.createBoundaryGeometry(gd, settings);
        } else if (gd.position) {
            // Point element
            geometry = new THREE.BoxGeometry(0.5, 0.5, 0.5);
        }

        if (!geometry) return null;

        const material = new THREE.MeshStandardMaterial({
            color: this.getCategoryColor(element.category)
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.userData = { id: element.id, category: element.category };

        // Apply position
        if (gd.position) {
            mesh.position.set(...(gd.position as [number, number, number]));
        } else if (gd.start) {
            mesh.position.set(...(gd.start as [number, number, number]));
        }

        return mesh;
    }

    private createExtrudedGeometry(gd: any, settings: any): THREE.BufferGeometry {
        const start = new THREE.Vector3(...gd.start);
        const end = new THREE.Vector3(...gd.end);
        const length = start.distanceTo(end);

        const width = (gd.width || gd.diameter || 200) / 1000;
        const height = (gd.height || gd.diameter || 200) / 1000;

        let shape: THREE.Shape;

        if (gd.diameter) {
            // Circular profile
            const radius = width / 2;
            const points: THREE.Vector2[] = [];
            for (let i = 0; i <= settings.segments; i++) {
                const angle = (i / settings.segments) * Math.PI * 2;
                points.push(new THREE.Vector2(Math.cos(angle) * radius, Math.sin(angle) * radius));
            }
            shape = new THREE.Shape(points);
        } else {
            // Rectangular profile
            shape = new THREE.Shape([
                new THREE.Vector2(-width / 2, -height / 2),
                new THREE.Vector2(width / 2, -height / 2),
                new THREE.Vector2(width / 2, height / 2),
                new THREE.Vector2(-width / 2, height / 2)
            ]);
        }

        const extrudeSettings = {
            steps: 1,
            depth: length,
            bevelEnabled: false
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        // Rotate to align with direction
        const direction = end.clone().sub(start).normalize();
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), direction);
        geometry.applyQuaternion(quaternion);

        return geometry;
    }

    private createBoundaryGeometry(gd: any, _settings: any): THREE.BufferGeometry {
        const shape = new THREE.Shape(
            gd.boundary.map((p: number[]) => new THREE.Vector2(p[0], p[1]))
        );

        const extrudeSettings = {
            steps: 1,
            depth: gd.thickness || 0.2,
            bevelEnabled: false
        };

        return new THREE.ExtrudeGeometry(shape, extrudeSettings);
    }

    private getCategoryColor(category: string): number {
        const colors: Record<string, number> = {
            'Walls': 0xcccccc,
            'Floors': 0x888888,
            'Roofs': 0xaa6633,
            'Doors': 0x8B4513,
            'Windows': 0x87CEEB,
            'Structural Columns': 0xff6600,
            'Structural Framing': 0xff9900,
            'Ducts': 0x00ff00,
            'Pipes': 0x0000ff,
            'Conduits': 0xffff00,
            'Precast Walls': 0xd8d2c6,
            'Precast Floors': 0xcfc8ba,
            'Precast Columns': 0xd8d2c6,
            'Precast Beams': 0xd8d2c6,
            'Precast Mounting Parts': 0xd9822b,
            'Precast Reinforcement': 0xb5541f
        };
        return colors[category] || 0x999999;
    }

    // ==================== OBJ Export ====================

    private exportToOBJ(meshes: THREE.Mesh[]): string {
        const lines: string[] = [];
        lines.push('# Exported from YAP BIM Engine');
        lines.push('# Vertices');

        let vertexOffset = 1;

        for (const mesh of meshes) {
            lines.push(`o ${mesh.userData.category}_${mesh.userData.id}`);

            const geometry = mesh.geometry as THREE.BufferGeometry;
            const position = geometry.getAttribute('position');
            const normal = geometry.getAttribute('normal');
            const index = geometry.index;

            // Export vertices
            for (let i = 0; i < position.count; i++) {
                const v = new THREE.Vector3(
                    position.getX(i),
                    position.getY(i),
                    position.getZ(i)
                );
                v.add(mesh.position);
                lines.push(`v ${v.x.toFixed(6)} ${v.y.toFixed(6)} ${v.z.toFixed(6)}`);
            }

            // Export normals
            if (normal) {
                for (let i = 0; i < normal.count; i++) {
                    lines.push(`vn ${normal.getX(i).toFixed(6)} ${normal.getY(i).toFixed(6)} ${normal.getZ(i).toFixed(6)}`);
                }
            }

            // Export faces
            if (index) {
                for (let i = 0; i < index.count; i += 3) {
                    const a = index.getX(i) + vertexOffset;
                    const b = index.getX(i + 1) + vertexOffset;
                    const c = index.getX(i + 2) + vertexOffset;
                    lines.push(`f ${a}//${a} ${b}//${b} ${c}//${c}`);
                }
            } else {
                for (let i = 0; i < position.count; i += 3) {
                    const a = i + vertexOffset;
                    const b = i + 1 + vertexOffset;
                    const c = i + 2 + vertexOffset;
                    lines.push(`f ${a}//${a} ${b}//${b} ${c}//${c}`);
                }
            }

            vertexOffset += position.count;
        }

        return lines.join('\n');
    }

    // ==================== STL Export ====================

    /**
     * Binary STL: 80-byte header + uint32 triangle count + 50 bytes per
     * facet. Fixed-size writes into one preallocated buffer beat the old
     * per-triangle ASCII string assembly (which allocated a string per
     * line and ballooned ~3x for large meshes).
     */
    private exportToSTL(meshes: THREE.Mesh[]): ArrayBuffer {
        let triangleCount = 0;
        for (const mesh of meshes) {
            const geometry = mesh.geometry as THREE.BufferGeometry;
            const index = geometry.index;
            const position = geometry.getAttribute('position');
            triangleCount += index ? Math.floor(index.count / 3) : Math.floor(position.count / 3);
        }

        const buffer = new ArrayBuffer(84 + triangleCount * 50);
        const view = new DataView(buffer);
        // Header bytes stay zero-filled (the spec ignores them).
        view.setUint32(80, triangleCount, true);

        let offset = 84;
        const writeVec = (x: number, y: number, z: number) => {
            view.setFloat32(offset, x, true);
            view.setFloat32(offset + 4, y, true);
            view.setFloat32(offset + 8, z, true);
            offset += 12;
        };

        for (const mesh of meshes) {
            const geometry = mesh.geometry as THREE.BufferGeometry;
            const position = geometry.getAttribute('position');
            const normal = geometry.getAttribute('normal');
            const index = geometry.index;

            const vertexAt = (i: number) => new THREE.Vector3(
                position.getX(i), position.getY(i), position.getZ(i)
            ).add(mesh.position);
            const normalAt = (i: number) => normal
                ? new THREE.Vector3(normal.getX(i), normal.getY(i), normal.getZ(i))
                : new THREE.Vector3(0, 0, 1);

            const writeTriangle = (a: number, b: number, c: number) => {
                const n = normalAt(a);
                writeVec(n.x, n.y, n.z);
                writeVec(vertexAt(a).x, vertexAt(a).y, vertexAt(a).z);
                writeVec(vertexAt(b).x, vertexAt(b).y, vertexAt(b).z);
                writeVec(vertexAt(c).x, vertexAt(c).y, vertexAt(c).z);
                view.setUint16(offset, 0, true);
                offset += 2;
            };

            if (index) {
                for (let i = 0; i + 2 < index.count; i += 3) {
                    writeTriangle(index.getX(i), index.getX(i + 1), index.getX(i + 2));
                }
            } else {
                for (let i = 0; i + 2 < position.count; i += 3) {
                    writeTriangle(i, i + 1, i + 2);
                }
            }
        }
        return buffer;
    }
}
